import { RIPSIM_ICONS } from "./../../../models/icon-config.model";
import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewEncapsulation } from "@angular/core";
import { ButtonModule } from "primeng/button";
import { GeneralDialogComponent } from "src/app/shared/components/dialogs/general-dialog/general-dialog.component";
import { RsIconComponent } from "../../icons/icon.component";
import { AUTH_EFFECT_REDIRECT_QUERYPARAM_IDENTIFIER_TOKEN } from "src/app/modules/auth/ngrx/auth.effects";
import { AuthService } from "src/app/modules/auth/shared/services/auth.service";
import { EXPIRED_SESSION_DIALOG_QUERY_PARAM } from "src/app/shared/components/dialogs/expired-session-dialog/expired-session-dialog.component";

@Component({
  selector: "rs-session-timeout-dialog",
  standalone: true,
  imports: [CommonModule, GeneralDialogComponent, ButtonModule, RsIconComponent],
  templateUrl: "./session-timeout-dialog.component.html",
  styleUrl: "./session-timeout-dialog.component.scss",
  encapsulation: ViewEncapsulation.None
})
export class SessionTimeoutDialogComponent implements OnChanges, OnDestroy {
  @Input() isActiveSession = false;
  visible = false;
  logoutCountdown = "0";
  RIPSIM_ICONS = RIPSIM_ICONS;

  private timeoutWebWorker?: Worker;
  private trackingActive = false;
  private readonly idleKeepAliveHandler = () => this.idleKeepAlive();

  constructor(private authService: AuthService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["isActiveSession"]) {
      if (this.isActiveSession) {
        this.startIdleTracking();
      } else {
        this.stopIdleTracking();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopIdleTracking();
  }

  handleVisibleChange(visible: boolean): void {
    this.visible = visible;
  }

  onLogout(displaySessionExpiredModal = false): void {
    this.visible = false;
    this.stopIdleTracking();

    if (displaySessionExpiredModal) {
      localStorage.setItem(
        AUTH_EFFECT_REDIRECT_QUERYPARAM_IDENTIFIER_TOKEN,
        JSON.stringify({ [EXPIRED_SESSION_DIALOG_QUERY_PARAM]: true })
      );
    }

    this.authService.logout();
  }

  onResume(): void {
    this.visible = false;
    this.sendKeepAlive();
  }

  private startIdleTracking(): void {
    if (this.trackingActive) return;

    this.trackingActive = true;
    this.initTimeoutWebWorker();
    window.addEventListener("mousemove", this.idleKeepAliveHandler, false);
    window.addEventListener("mousedown", this.idleKeepAliveHandler, false);
    window.addEventListener("keydown", this.idleKeepAliveHandler, false);
  }

  private stopIdleTracking(): void {
    if (this.trackingActive) {
      window.removeEventListener("mousemove", this.idleKeepAliveHandler, false);
      window.removeEventListener("mousedown", this.idleKeepAliveHandler, false);
      window.removeEventListener("keydown", this.idleKeepAliveHandler, false);
      this.trackingActive = false;
    }

    this.visible = false;
    if (this.timeoutWebWorker) {
      this.timeoutWebWorker.postMessage({ command: "stop" });
      this.timeoutWebWorker.terminate();
      this.timeoutWebWorker = undefined;
    }
  }

  private initTimeoutWebWorker(): void {
    if (this.timeoutWebWorker) return;

    this.timeoutWebWorker = new Worker(new URL("../../../webworkers/timeout.worker", import.meta.url), {
      name: "SessionTimeoutDialog_timeout.worker",
      type: "module"
    });
    this.timeoutWebWorker.onmessage = ({ data }) => {
      if (data.command === "warning") {
        this.logoutCountdown = `${data.logoutTime}`;
        if (!this.visible) {
          this.visible = true;
        }
      } else if (data.command === "logout") {
        this.onLogout(true);
      }
    };
    this.timeoutWebWorker.postMessage({ command: "start" });
  }

  private sendKeepAlive(): void {
    if (!this.timeoutWebWorker) return;
    this.timeoutWebWorker.postMessage({
      command: "keepalive"
    });
    void this.authService.renewTokenIfStale();
  }

  private idleKeepAlive(): void {
    if (!this.visible && this.isActiveSession) {
      this.sendKeepAlive();
    }
  }
}
